import apiClient from './api';
import type { DineInTableInfo } from './restaurantService';
import type { GuestOrderingStatus } from '../utils/guestOrdering';
import { notifyCartUpdated } from '../utils/cartEvents';
import { trackEvent } from '../utils/analytics';
import { dedupeAsync, invalidateDedupeCache } from '../utils/requestDedupe';

function invalidateCartCache(appName: string): void {
  invalidateDedupeCache(`cart:${appName}`);
  invalidateDedupeCache(`cart-count:${appName}`);
}

/**
 * The server pricing engine's breakdown of the cart (docs/ORDER_PRICING_CONTRACT.md) -- the same
 * code that prices the order at placement, so the guest pays exactly `grandTotal`. The UI only
 * displays these figures; it never computes tax, service charge or rounding itself.
 */
export interface CartPricing {
  subTotal: number;
  promotionDiscount: number;
  cartPromotionDiscount?: number;
  menuPromotionSavings?: number;
  promotionId?: number | null;
  promotionName?: string | null;
  offerDiscount: number;
  couponDiscount: number;
  couponCode?: string | null;
  couponSource?: string | null;
  couponRejected?: boolean;
  couponMessage?: string | null;
  manualDiscount: number;
  deliveryCharges: number;
  packingCharges: number;
  serviceCharge: number;
  serviceChargePercent: number;
  /** Dine-in with a configured rate: show the line and let the guest remove it (CCPA 2022). */
  serviceChargeApplicable: boolean;
  serviceChargeApplied: boolean;
  serviceChargeOptional: boolean;
  taxableValue: number;
  cgstPercent: number;
  sgstPercent: number;
  cgstAmount: number;
  sgstAmount: number;
  netTotal: number;
  roundOff: number;
  grandTotal: number;
}

export interface CartDetails {
  totalItemCount: number;
  totalItemPrice: number;
  totalDiscountedPrice: number;
  couponDiscount?: number;
  offerDiscount?: number;
  appliedPromotionId?: number;
  appliedPromotionName?: string;
  appliedCouponCode?: string;
  promotionInclusiveSubtotal?: number;
  cgstPercent?: number;
  sgstPercent?: number;
  /** True when the delivery fee / packing of the given quote are included in `pricing`. */
  deliveryQuoteApplied?: boolean;
  pricing?: CartPricing;
}

/** Inputs of the cart quote that the guest controls; never a price. */
export interface CartQuoteOptions {
  serviceChargeOptOut?: boolean;
  quoteId?: string;
  addressId?: number;
}

export interface PlaceOrderExtras {
  quoteId?: string;
  addressId?: number;
  paymentMethod?: string;
  kitchenNote?: string;
  orderSource?: string;
  kioskSessionId?: string | number;
  kioskTableId?: number;
  /** Guest removed the optional dine-in service charge. */
  serviceChargeOptOut?: boolean;
  /** The grand total shown; the server answers 409 if it can no longer honour it. */
  expectedGrandTotal?: number;
}

export interface PlaceOrderResponse {
  success: boolean;
  orderId?: number;
  grandTotal?: number;
  message?: string;
  code?: string;
  redirectTo?: string;
  existingOrderId?: number;
  priceChanged?: boolean;
  /** Something the guest must know, e.g. their code no longer applies to the updated bill. */
  notice?: string;
  queueTokenLabel?: string;
}

export interface CartCountResponse {
  success: boolean;
  cartCount: number;
  cartDetails: CartDetails;
  message?: string;
}

/**
 * Identity of a cart line beyond the menu item (design D5/D10): variant, add-on selection and
 * kitchen note. The same menu item with different options is a separate line.
 */
export interface CartOptions {
  sizeTypeId?: number;
  modifierOptionIds?: number[];
  itemNote?: string;
}

export interface CartLineModifier {
  id: number;
  name?: string;
  groupId?: number;
  groupName?: string;
  groupType?: string;
  price?: number;
}

/** Error codes the server returns (HTTP 400) for an invalid variant / add-on / note. */
export type CartLineErrorCode = 'VARIANT_UNAVAILABLE' | 'MODIFIER_INVALID' | 'NOTE_TOO_LONG';

export interface CartItem {
  cartId: number;
  menuId: number;
  quantity: number;
  sizeTypeId?: number;
  /** Variant name; null for single-variant items. */
  variantName?: string | null;
  variantLabel?: string;
  /** Sorted, comma-joined option ids; "" = no add-ons. */
  modifierSignature?: string;
  modifierOptionIds?: number[];
  modifiers?: CartLineModifier[];
  /** Add-on amount per unit (already included in menu.sellingPrice). */
  modifierAmount?: number;
  itemNote?: string | null;
  /** Variant and add-ons joined with " · ". */
  optionsSummary?: string | null;
  hasOptions?: boolean;
  menu: {
    id: number;
    item: string;
    itemUrl?: string;
    price: number;
    mrp?: number;
    sellingPrice?: number;
    effectiveSellingPrice?: number;
    imageBig?: string;
  };
  itemTotal: number;
  baseUnitPrice?: number;
  effectiveUnitPrice?: number;
  baseItemTotal?: number;
  sizeTypeName?: string;
}

export interface CartResponse {
  success: boolean;
  cartItems: CartItem[];
  cartDetails: CartDetails;
  restaurant: {
    id: number;
    appName: string;
    restaurantName: string;
    currencySymbol?: string;
  };
  dineInTable?: DineInTableInfo;
  guestOrdering?: GuestOrderingStatus;
  message?: string;
}

export interface UpdateCartRequest {
  menuId: number;
  quantity: number;
  orderType?: number;
  sizeTypeId?: number;
}

export interface UpdateCartResponse {
  success: boolean;
  cartCount: number;
  cartDetails: CartDetails;
  message?: string;
}

export interface AddToCartRequest extends CartOptions {
  menuId: number;
  quantity: number;
  orderType?: number;
}

export interface AddToCartResponse {
  success: boolean;
  cartCount: number;
  cartDetails: CartDetails;
  message?: string;
  code?: CartLineErrorCode | string;
}

type ErrorWithResponse = { response?: { status?: number; data?: unknown } };

/** Message from a failed cart / order request, or the fallback. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const data = (err as ErrorWithResponse)?.response?.data as { message?: string } | undefined;
  return (data && typeof data.message === 'string' && data.message) || fallback;
}

class CartService {
  /**
   * Sets the quantity of one cart line, identified by menu item + options (0 removes the line).
   * Resolves with {success:false, code, message} when the server rejects the options.
   */
  async addToCart(
    appName: string,
    menuId: number,
    quantity: number,
    orderType: number = 1,
    options?: CartOptions
  ): Promise<AddToCartResponse> {
    const params = new URLSearchParams();
    params.append('menuId', menuId.toString());
    params.append('quantity', quantity.toString());
    params.append('orderType', orderType.toString());
    if (options?.sizeTypeId != null) {
      params.append('sizeTypeId', options.sizeTypeId.toString());
    }
    (options?.modifierOptionIds || []).forEach((id) => {
      if (id != null && id > 0) {
        params.append('modifierOptionIds', String(id));
      }
    });
    const note = options?.itemNote?.trim();
    if (note) {
      params.append('itemNote', note);
    }

    let data: AddToCartResponse;
    try {
      const response = await apiClient.post(
        `/api/restaurants/${appName}/cart/add`,
        params,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );
      data = response.data;
    } catch (err: unknown) {
      // Invalid variant / add-on / note: the server answers 400 {success:false, code, message}.
      const res = (err as ErrorWithResponse)?.response;
      const body = res?.data as AddToCartResponse | undefined;
      if (res?.status === 400 && body && body.success === false) {
        return body;
      }
      throw err;
    }
    if (data.success) {
      invalidateCartCache(appName);
      notifyCartUpdated(appName, data.cartCount);
      trackEvent(quantity > 0 ? 'add_to_cart' : 'remove_from_cart', {
        restaurant_app: appName,
        item_id: menuId,
        quantity,
        order_type: orderType,
      });
    }
    return data;
  }

  async getCartCount(appName: string): Promise<CartCountResponse> {
    return dedupeAsync(`cart-count:${appName}`, async () => {
      const response = await apiClient.get(`/api/restaurants/${appName}/cart/count`);
      return response.data;
    }, 4_000);
  }

  async getCart(appName: string, orderType?: number, quote?: CartQuoteOptions): Promise<CartResponse> {
    const params: Record<string, string | number | boolean> = {};
    if (orderType != null) {
      params.orderType = orderType;
    }
    if (quote?.serviceChargeOptOut) {
      params.serviceChargeOptOut = true;
    }
    if (quote?.quoteId && quote.addressId) {
      params.quoteId = quote.quoteId;
      params.addressId = quote.addressId;
    }
    const key = Object.keys(params)
      .sort()
      .map((k) => `${k}=${params[k]}`)
      .join('&');
    return dedupeAsync(`cart:${appName}:${key || 'any'}`, async () => {
      const response = await apiClient.get(`/api/restaurants/${appName}/cart`, {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
      return response.data;
    }, 4_000);
  }

  /**
   * Places the cart. Pricing is the server's: only the guest's choices are sent. A 409 means the
   * price shown can no longer be honoured (code used up, total changed); it resolves with the
   * server's message and `priceChanged: true` so the cart can refresh and ask again.
   */
  async placeOrder(
    appName: string,
    orderType: number,
    extras?: PlaceOrderExtras
  ): Promise<PlaceOrderResponse> {
    const requestBody: Record<string, unknown> = { type: orderType };
    if (extras?.quoteId) {
      requestBody.quoteId = extras.quoteId;
    }
    if (extras?.addressId) {
      requestBody.addressId = extras.addressId;
    }
    if (extras?.paymentMethod) {
      requestBody.paymentMethod = extras.paymentMethod;
    }
    if (extras?.kitchenNote && extras.kitchenNote.trim()) {
      requestBody.kitchenNote = extras.kitchenNote.trim();
    }
    if (extras?.orderSource) {
      requestBody.orderSource = extras.orderSource;
    }
    if (extras?.kioskSessionId != null && extras.kioskSessionId !== '') {
      requestBody.kioskSessionId = extras.kioskSessionId;
    }
    if (extras?.kioskTableId != null) {
      requestBody.kioskTableId = extras.kioskTableId;
    }
    if (extras?.serviceChargeOptOut) {
      requestBody.serviceChargeOptOut = true;
    }
    if (typeof extras?.expectedGrandTotal === 'number' && Number.isFinite(extras.expectedGrandTotal)) {
      requestBody.expectedGrandTotal = extras.expectedGrandTotal;
    }
    try {
      const response = await apiClient.post(`/api/restaurants/${appName}/place-order`, requestBody);
      invalidateCartCache(appName);
      const placed = response.data as PlaceOrderResponse;
      if (placed?.success && placed.orderId) {
        trackEvent('purchase', {
          restaurant_app: appName,
          transaction_id: placed.orderId,
          order_type: orderType,
          value: extras?.expectedGrandTotal ?? placed.grandTotal,
          payment_method: extras?.paymentMethod,
          currency: 'INR',
        });
      }
      return placed;
    } catch (err: unknown) {
      const res = (err as ErrorWithResponse)?.response;
      const body = res?.data as PlaceOrderResponse | undefined;
      if (res?.status === 409 && body) {
        invalidateCartCache(appName);
        return { ...body, success: false, priceChanged: true };
      }
      throw err;
    }
  }

  async updateCart(appName: string, items: UpdateCartRequest[]): Promise<UpdateCartResponse> {
    const response = await apiClient.post(`/api/restaurants/${appName}/cart/update`, items);
    const data = response.data;
    if (data.success) {
      invalidateCartCache(appName);
      notifyCartUpdated(appName, data.cartCount);
      items.forEach((item) => {
        trackEvent(item.quantity > 0 ? 'add_to_cart' : 'remove_from_cart', {
          restaurant_app: appName,
          item_id: item.menuId,
          quantity: item.quantity,
          order_type: item.orderType,
        });
      });
    }
    return data;
  }

  async deleteCartItem(appName: string, cartId: number | string, menuId?: number): Promise<{ success: boolean; message?: string }> {
    const params: any = {};
    if (menuId) {
      params.menuId = menuId;
    }
    const response = await apiClient.delete(`/api/restaurants/${appName}/cart/item/${cartId}`, { params });
    const data = response.data;
    if (data.success) {
      invalidateCartCache(appName);
      notifyCartUpdated(appName);
      trackEvent('remove_from_cart', {
        restaurant_app: appName,
        item_id: menuId,
      });
    }
    return data;
  }

  /** Eligibility and amount are computed by the server from the actual cart. */
  async applyCoupon(
    appName: string,
    couponCode: string,
    orderType: number = 1,
    serviceChargeOptOut: boolean = false
  ): Promise<{ success: boolean; message?: string; discountAmount?: number; code?: string; pricing?: CartPricing }> {
    try {
      const response = await apiClient.post(`/api/restaurants/${appName}/cart/coupon/apply`, {
        couponCode,
        orderType,
        serviceChargeOptOut,
      });
      invalidateCartCache(appName);
      return response.data;
    } catch (err: unknown) {
      const res = (err as ErrorWithResponse)?.response;
      const body = res?.data as { success: boolean; message?: string } | undefined;
      if (res?.status === 400 && body) {
        return { ...body, success: false };
      }
      throw err;
    }
  }

  async removeCoupon(appName: string): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.post(`/api/restaurants/${appName}/cart/coupon/remove`);
    invalidateCartCache(appName);
    return response.data;
  }

  async getActivePromotions(appName: string): Promise<{
    success: boolean;
    promotions: Array<{
      id: number;
      name: string;
      type?: string;
      description?: string;
      category?: string;
      discountPreview?: string;
    }>;
  }> {
    const response = await apiClient.get(`/api/restaurants/${appName}/promotions/active`);
    return response.data;
  }
}

export default new CartService();
